import { Img, Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey50, grey700, grey920, transparent } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { BASE_ROUTES, VERTICAL_HOME } from '../../constants/routes'
import { CardItemModel } from '../../types/flo-experience'
import { CARD_SPACING } from '../../utils/carouselUtils'
import { getIcon } from '../../utils/image'
import { captureClick, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import { calculateRenderInfo } from '../../utils/text'
import SpeechComp from '../speechComp/speechComp'
import { HIDDEN, INNER_MARGIN, TvCardImgPlaceholder } from './templates'
import { EXPERIENCES } from './TvNavigationCard'

export default class TvFavoriteCard extends SpeechComp {
  private _data: CardItemModel
  private _style: string
  private _cardType: string
  private _rowIndex = 0
  private _padding = 106
  private _cardIndex: number

  static _template() {
    return {
      w: 0,
      h: 120,
      rect: true,
      collision: true,
      color: getColor(grey920),
      flexItem: {
        marginRight: CARD_SPACING,
        maxWidth: 538 + INNER_MARGIN,
      },
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: 16,
      },
      Container: {
        h: (h) => h,
        w: (w) => w,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          stroke: 4,
          strokeColor: transparent,
          fillColor: transparent,
          radius: 16,
        },
        InnerContainer: {
          h: 88,
          x: 16,
          y: 16,
          flex: {
            direction: 'row',
            alignItems: 'center',
          },
          shader: null,
          Asset: {
            w: 88,
            h: 88,
            rect: true,
            color: getColor(grey700),
            flexItem: { marginRight: 24 },
            shader: {
              type: Lightning.shaders.RoundedRectangle,
              radius: 12,
            },
            Placeholder: {
              type: TvCardImgPlaceholder,
              imgSize: { w: 80, h: 72 },
            },
            Icon: {
              x: (w) => w / 2,
              y: (h) => h / 2,
              mount: 0.5,
              color: getColor(grey50),
              texture: null,
              alpha: HIDDEN,
            },
            ImageWrapper: {
              w: (w) => w,
              h: (h) => h,
              rtt: true,
              color: transparent,
              shader: {
                type: Lightning.shaders.RoundedRectangle,
                radius: 12,
              },
              Image: {
                w: (w) => w,
                h: (h) => h,
                texture: null,
                alpha: HIDDEN,
              },
            },
          },
          TextWrapper: {
            y: 1,
            flex: {
              alignItems: 'center',
              justifyContent: 'center',
            },
            Label: {
              text: {
                text: '',
                textColor: getColor(grey50),
                fontFace: UniNeueBold,
                fontSize: 32,
                maxLines: 2,
                maxLinesSuffix: '...',
                wordWrapWidth: 340,
              },
            },
          },
          FavoriteIcon: {
            h: 36,
            w: 36,
            color: getColor(grey50),
            flexItem: { marginLeft: 24 },
          },
        },
      },
    }
  }

  get container() {
    return this.tag('Container')
  }

  get innerContainer() {
    return this.tag('InnerContainer')
  }

  get label() {
    return this.tag('Label')
  }

  get icon() {
    return this.tag('Icon')
  }

  get image() {
    return this.tag('Image')
  }

  get placeholder() {
    return this.tag('Placeholder')
  }

  set data(v) {
    this._data = v
  }

  get data() {
    return this._data
  }

  set style(v) {
    this._style = v
  }

  get style() {
    return this._style
  }

  set cardType(v) {
    this._cardType = v
  }

  get cardType() {
    return this._cardType
  }

  set rowIndex(v) {
    this._rowIndex = v
  }

  get rowIndex() {
    return this._rowIndex
  }

  get padding() {
    return this._padding
  }

  get speechText(): string {
    return this.data?.text?.text
  }

  get cardIndex() {
    return this._cardIndex
  }

  set cardIndex(v) {
    this._cardIndex = v
  }

  _init() {
    this.label.on('txLoaded', () => {
      this.w = this.label.renderWidth + this.padding * 2
    })

    this.image.on('txLoaded', () => {
      this.placeholder.alpha = 0
      this.image.setSmooth('alpha', 1)
    })

    this.icon.on('txLoaded', () => {
      this.placeholder.alpha = 0
      this.icon.setSmooth('alpha', 1)
    })
  }

  _active() {
    this.renderCardData()
  }

  _focus() {
    this.container.patch({
      shader: {
        strokeColor: getColor(grey50),
      },
    })
    this.speak()
  }

  _unfocus() {
    this.container.patch({
      shader: {
        strokeColor: transparent,
      },
    })
  }

  _handleEnter() {
    void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, this.data?.action?.analytics)

    if (this.data?.action?.url.includes(BASE_ROUTES.FAVORITE_HUB)) {
      const searchUrl = this.data?.action?.url
      const encodedUrl = encodeURIComponent(searchUrl)
      Router.navigate(`${BASE_ROUTES.FAVORITE_HUB}/${encodedUrl}`)
    } else if (this.data?.action?.url?.includes(EXPERIENCES.HOME)) {
      const urlParamsList = this.data?.action?.url?.split('?')[1]
      const urlParams = new URLSearchParams(urlParamsList)
      const siteId = urlParams.get('site_id')
      urlParams.delete('site_id')
      urlParams.delete('nav_id')
      const verticalHomeUrl = `${VERTICAL_HOME.replace(':siteId', siteId)}?${urlParams.toString()}`
      Router.navigate(verticalHomeUrl)
    }
  }

  renderCardData() {
    const isIcon = this.data?.leading_decorator?.type === 'icon'
    const labelText = this.getTruncatedLabelText()

    this.innerContainer.patch({
      Asset: {
        ...(isIcon && {
          Icon: {
            texture: Lightning.Tools.getSvgTexture(
              getIcon(this.data?.leading_decorator?.style),
              56,
              56,
            ),
          },
        }),
        ...(!isIcon && {
          ImageWrapper: {
            Image: {
              texture: Img(this.data?.leading_decorator?.url)?.cover(88, 88),
            },
          },
        }),
      },
      TextWrapper: {
        Label: {
          text: labelText ?? '',
        },
      },
      FavoriteIcon: {
        texture: Lightning.Tools.getSvgTexture(
          getIcon(this.data?.trailing_decorator?.style),
          36,
          36,
        ),
      },
    })
  }

  getTruncatedLabelText() {
    const textRenderInfo = calculateRenderInfo(this.label.text, this.data?.text?.text)
    return textRenderInfo.lines.join(' ')
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this._unfocus()
  }

  _handleClick() {
    this._handleEnter()
  }
}
